import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { api, type NewsJobStatusData, type ReceiptJobStatusData } from "../lib/api";
import { storage } from "../lib/storage";
import { AppError, logError } from "../lib/errors";
import {
  JOB_NOTIFICATION_EVENT,
  getPendingJobNotificationKey,
  readPendingJobNotifications,
  removePendingJobNotification,
  type PendingJobNotification,
} from "../lib/jobNotifications";
import {
  clearPendingNewsJobSnapshot,
  formatNewsJobStageLabel,
  getNewsNotificationTypeLabel,
  readPendingNewsJobSnapshot,
  toPendingNewsJobNotification,
  writePendingNewsJobNotification,
} from "../lib/newsJob";
import {
  clearPendingReceiptJobSnapshot,
  formatReceiptStageLabel,
  getReceiptPollIntervalMs,
  readPendingReceiptJobSnapshot,
  writePendingReceiptJobNotification,
} from "../lib/receipt";

type JobToastState = {
  key: string;
  tone: "success" | "error";
  title: string;
  detail?: string;
  notification: PendingJobNotification;
};

function getJobFailureMessage(failedError?: string) {
  const message = (failedError ?? "").trim();
  if (!message) return "結果を確認できませんでした。";

  const lowered = message.toLowerCase();
  if (
    lowered.includes("valueerror") ||
    lowered.includes("did not include a json object") ||
    lowered.includes("gemini evidence bundle")
  ) {
    return "ニュース内容の生成に失敗しました。しばらくしてから再試行してください。";
  }

  return message;
}

function buildJobToast(notification: PendingJobNotification): JobToastState {
  if (notification.kind === "level_up") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: "ランクアップしました",
      detail: `親密度は${notification.rankName}になりました`,
      notification,
    };
  }

  if (notification.kind === "mode_unlock") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: "success",
      title: `${notification.modeName ?? "新しい会話モード"}が解放されました`,
      detail: "新しい会話モードを試してください。",
      notification,
    };
  }

  if (notification.kind === "receipt") {
    return {
      key: getPendingJobNotificationKey(notification),
      tone: notification.status === "completed" ? "success" : "error",
      title:
        notification.status === "completed"
          ? "領収書の処理が完了しました"
          : "領収書の処理に失敗しました",
      detail:
        notification.status === "completed"
          ? notification.extractedSummary ??
            "Google Drive と Google スプレッドシートへの保存が完了しました。"
          : notification.failedStage
            ? `${getJobFailureMessage(notification.failedError)} (${formatReceiptStageLabel(
                notification.failedStage
              )})`
            : getJobFailureMessage(notification.failedError),
      notification,
    };
  }

  return {
    key: getPendingJobNotificationKey(notification),
    tone: notification.status === "completed" ? "success" : "error",
    title:
      notification.status === "completed"
        ? "ニュース送信の処理が完了しました"
        : "ニュース送信の処理に失敗しました",
    detail:
      (() => {
        const contentLabel = notification.mode === "report" ? "レポート" : "ニュース";

        if (notification.status === "completed") {
          if (notification.keyword) {
            return `「${notification.keyword}」の${contentLabel}を${getNewsNotificationTypeLabel(
              notification.notificationType
            )}へ送信しました。`;
          }

          return `${getNewsNotificationTypeLabel(notification.notificationType)} への${contentLabel}送信が完了しました。`;
        }

        if (notification.failedStage) {
          return `${getJobFailureMessage(notification.failedError)} (${formatNewsJobStageLabel(
            notification.failedStage
          )})`;
        }

        return getJobFailureMessage(notification.failedError);
      })(),
    notification,
  };
}

function isJobNotFoundError(error: unknown) {
  return error instanceof AppError && error.code === "NOT_FOUND";
}

function toPendingReceiptNotification(response: ReceiptJobStatusData) {
  if (response.status !== "completed" && response.status !== "failed") {
    return null;
  }

  return {
    jobId: response.jobId,
    status: response.status,
    processedAt: response.processedAt ?? undefined,
    extractedSummary: response.extractedSummary ?? undefined,
    failedStage: response.failedStage ?? undefined,
    failedError: response.failedError ?? undefined,
  } as const;
}

export function JobNotificationCenter() {
  const location = useLocation();
  const receiptPollTimerRef = useRef<number | null>(null);
  const newsPollTimerRef = useRef<number | null>(null);
  const [notifications, setNotifications] = useState<PendingJobNotification[]>([]);

  const syncNotifications = useCallback(() => {
    setNotifications(readPendingJobNotifications());
  }, []);

  const dismissToast = useCallback((notification: PendingJobNotification) => {
    if (notification.kind === "mode_unlock") {
      storage.addAcknowledgedUnlockedMode(notification.characterId, notification.mode);
    }
    removePendingJobNotification(notification);
  }, []);

  const stopReceiptPolling = useCallback(() => {
    if (receiptPollTimerRef.current !== null) {
      window.clearTimeout(receiptPollTimerRef.current);
      receiptPollTimerRef.current = null;
    }
  }, []);

  const stopNewsPolling = useCallback(() => {
    if (newsPollTimerRef.current !== null) {
      window.clearTimeout(newsPollTimerRef.current);
      newsPollTimerRef.current = null;
    }
  }, []);

  const handleResolvedReceiptJob = useCallback(
    (response: ReceiptJobStatusData) => {
      const notification = toPendingReceiptNotification(response);
      if (!notification) {
        return;
      }

      clearPendingReceiptJobSnapshot();
      stopReceiptPolling();
      writePendingReceiptJobNotification(notification);
    },
    [stopReceiptPolling]
  );

  const handleResolvedNewsJob = useCallback(
    (response: NewsJobStatusData) => {
      const pendingSnapshot = readPendingNewsJobSnapshot();
      const notification = toPendingNewsJobNotification(response, {
        mode: pendingSnapshot?.mode,
      });
      if (!notification) {
        return;
      }

      clearPendingNewsJobSnapshot();
      stopNewsPolling();
      writePendingNewsJobNotification(notification);
    },
    [stopNewsPolling]
  );

  const scheduleReceiptJobPoll = useCallback(
    (jobId: string, startedAtMs: number, clientRequestId?: string) => {
      stopReceiptPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      receiptPollTimerRef.current = window.setTimeout(async () => {
        try {
          const response = await api.getReceiptJob(jobId);

          if (response.status === "accepted") {
            scheduleReceiptJobPoll(jobId, startedAtMs, response.clientRequestId ?? clientRequestId);
            return;
          }

          handleResolvedReceiptJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingReceiptJobSnapshot();
            stopReceiptPolling();
            return;
          }

          logError(error);
          scheduleReceiptJobPoll(jobId, startedAtMs, clientRequestId);
        }
      }, pollIntervalMs);
    },
    [handleResolvedReceiptJob, stopReceiptPolling]
  );

  const scheduleNewsJobPoll = useCallback(
    (jobId: string, startedAtMs: number) => {
      stopNewsPolling();
      const pollIntervalMs = getReceiptPollIntervalMs(Math.max(0, Date.now() - startedAtMs));

      newsPollTimerRef.current = window.setTimeout(async () => {
        try {
          const response = await api.getNewsJob(jobId);

          if (response.status === "accepted") {
            scheduleNewsJobPoll(jobId, startedAtMs);
            return;
          }

          handleResolvedNewsJob(response);
        } catch (error) {
          if (isJobNotFoundError(error)) {
            clearPendingNewsJobSnapshot();
            stopNewsPolling();
            return;
          }

          logError(error);
          scheduleNewsJobPoll(jobId, startedAtMs);
        }
      }, pollIntervalMs);
    },
    [handleResolvedNewsJob, stopNewsPolling]
  );

  const refreshPendingReceiptJob = useCallback(async () => {
    const pendingSnapshot = readPendingReceiptJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopReceiptPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopReceiptPolling();

    try {
      const response = await api.getReceiptJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleReceiptJobPoll(
          pendingSnapshot.jobId,
          safeStartedAtMs,
          response.clientRequestId ?? pendingSnapshot.clientRequestId
        );
        return;
      }

      handleResolvedReceiptJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingReceiptJobSnapshot();
        stopReceiptPolling();
        return;
      }

      logError(error);
      scheduleReceiptJobPoll(
        pendingSnapshot.jobId,
        safeStartedAtMs,
        pendingSnapshot.clientRequestId
      );
    }
  }, [handleResolvedReceiptJob, scheduleReceiptJobPoll, stopReceiptPolling]);

  const refreshPendingNewsJob = useCallback(async () => {
    const pendingSnapshot = readPendingNewsJobSnapshot();
    if (!pendingSnapshot?.jobId) {
      stopNewsPolling();
      return;
    }

    const startedAtMs = Date.parse(pendingSnapshot.startedAt);
    const safeStartedAtMs = Number.isFinite(startedAtMs) ? startedAtMs : Date.now();

    stopNewsPolling();

    try {
      const response = await api.getNewsJob(pendingSnapshot.jobId);

      if (response.status === "accepted") {
        scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
        return;
      }

      handleResolvedNewsJob(response);
    } catch (error) {
      if (isJobNotFoundError(error)) {
        clearPendingNewsJobSnapshot();
        stopNewsPolling();
        return;
      }

      logError(error);
      scheduleNewsJobPoll(pendingSnapshot.jobId, safeStartedAtMs);
    }
  }, [handleResolvedNewsJob, scheduleNewsJobPoll, stopNewsPolling]);

  useEffect(() => {
    syncNotifications();
  }, [syncNotifications]);

  useEffect(() => {
    const handleNotificationEvent = () => {
      syncNotifications();
    };

    window.addEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);

    return () => {
      window.removeEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);
    };
  }, [syncNotifications]);

  useEffect(() => {
    if (location.pathname === "/receipt") {
      stopReceiptPolling();
    } else {
      void refreshPendingReceiptJob();
    }

    void refreshPendingNewsJob();

    const handleFocus = () => {
      if (location.pathname !== "/receipt") {
        void refreshPendingReceiptJob();
      }
      void refreshPendingNewsJob();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleFocus();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopReceiptPolling();
      stopNewsPolling();
    };
  }, [
    location.pathname,
    refreshPendingNewsJob,
    refreshPendingReceiptJob,
    stopNewsPolling,
    stopReceiptPolling,
  ]);

  if (!notifications.length) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex justify-center px-4 sm:justify-end sm:px-6">
      <div className="flex w-full max-w-md flex-col gap-3">
        {notifications.map((notification) => {
          const toast = buildJobToast(notification);

          return (
            <div
              key={toast.key}
              className={`pointer-events-auto rounded-[1.4rem] border px-4 py-3 shadow-[0_18px_40px_rgba(74,62,57,0.16)] ${
                toast.tone === "success"
                  ? "border-[#D8E7D3] bg-[#F7FBF3] text-[#4B6650]"
                  : "border-[#E8B2A8] bg-[#FFF3F1] text-[#9A4B40]"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-black">{toast.title}</p>
                  {toast.detail ? <p className="mt-1 text-sm leading-6">{toast.detail}</p> : null}
                </div>
                <button
                  type="button"
                  onClick={() => dismissToast(notification)}
                  className="shrink-0 rounded-full border border-current/20 bg-white/70 px-3 py-1 text-xs font-bold transition hover:bg-white"
                >
                  確認
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
